import { Card, Col, Form, Radio, Row, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetProduct } from "../hooks/use-get-product";
import Input from "antd/es/input";
import VuiFormActionButton from "../../../components/form-action-button";
import { useCallback, useEffect, useState } from "react";
import VuiPageTitle from "../../../components/page-title";
import { useFormProduct } from "../hooks/use-form-product";
import { ProductFormData, ProductPayloadType } from "./interface";
import VuiSelect from "../../../components/select";
import { openNotification } from "../../../utils/helpers";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { convertToSelectValue } from "../../../components/select/function";
import { ValueType } from "../../../components/select/interface";
import UnitRepository from "../../../repositories/unit-repository";

const { TextArea } = Input;
const { Text } = Typography;

const ProductFormModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const title = id
    ? t("pages.product.detail.title")
    : t("pages.product.add.title");
  const { loadProductData, isOnFetchingProductData, productData } =
    useGetProduct();
  const { submitLoading, onSubmit, onDelete, deleteLoading } = useFormProduct();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [form] = Form.useForm<ProductFormData>();

  const onFinish = useCallback(
    async (data: ProductFormData) => {
      const normalizeData: ProductPayloadType = {
        ...data,
        unit_ids: data.unit_ids?.map((unit) => Number(unit.value)),
      };

      await onSubmit(normalizeData, id);
    },
    [id, onSubmit]
  );

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.product"),
          })
        );
        navigate(`/other-list/product`);
      });
    }
  }, [id, navigate, onDelete, t]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadProductData(id, { with: "units" });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && productData) {
      form.setFieldsValue({
        ...productData,
        unit_ids: productData.units.map((unit) =>
          convertToSelectValue(unit)
        ) as ValueType[],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData]);

  return (
    <>
      <VuiPageTitle title={title} onBackLink="/other-list/product" />

      <Spin size="large" spinning={isOnFetchingProductData}>
        <Form
          form={form}
          layout={"vertical"}
          initialValues={{
            is_active: true,
          }}
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col md={18} xs={24}>
              <Card title={t("common.text.information")}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label={t("common.form.productName.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.productName.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("common.form.productName.placeholder")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="unit_ids"
                      label={t("common.form.unit.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.unit.label"),
                          }),
                        },
                      ]}
                    >
                      <VuiSelect mode="multiple" repository={UnitRepository} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col md={6} xs={24}>
              <Card title={t("common.text.status")}>
                <Form.Item name="is_active">
                  <Radio.Group>
                    <Radio value={true}>Active</Radio>
                    <Radio value={false}>Not Active</Radio>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>

            <Col md={24} xs={24}>
              <VuiFormActionButton
                cancelBtnLink="/other-list/product"
                isLoading={submitLoading}
              />
            </Col>

            {id && (
              <VuiAccessible access="product.destroy">
                <Col xs={24}>
                  <Text
                    className="cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    title={t("common.text.delete")}
                    type="secondary"
                  >
                    {t("common.text.delete")}
                  </Text>
                </Col>
              </VuiAccessible>
            )}
          </Row>
        </Form>
      </Spin>

      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
    </>
  );
};

export default ProductFormModule;
